import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { QRCodeCanvas } from 'qrcode.react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  enrollmentApi,
  useCreateOrderMutation,
  useGetMyStudentsQuery,
  useGetOrderStatusQuery,
} from '../../api/enrollment'
import { useLoader } from '../../components/Loader'
import { EnrollStudent, OrderStatus } from '../../models/enrollment'
import { ineligibleMessage } from '../../messages/eligibility'

const formatVnd = (amount: number) => new Intl.NumberFormat('vi-VN').format(amount) + ' đ'

const steps = ['Chọn đoàn sinh', 'Xác nhận', 'Thanh toán']

const EnrollmentWizard: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const existingCode = searchParams.get('code')

  // Dev-only preview of the payment/QR step without a backend or login. Stripped from
  // production builds because CRA sets NODE_ENV=production (this branch is dead code there).
  const previewMode =
    process.env.NODE_ENV === 'development' && searchParams.get('preview') === 'qr'
  const previewOrder: OrderStatus = {
    paymentCode: 'GD26PREVIEW',
    status: 'PENDING',
    expectedAmount: 20000,
    qr: {
      emvString:
        '00020101021238570010A00000072701270006970423011320778872093080208QRIBFTTA53037045802VN5910BACH MINH NAM6304ABCD',
      paymentCode: 'GD26PREVIEW',
      amount: 20000,
      accountNo: '20778872093',
      accountName: 'BACH MINH NAM',
      bin: '970423',
    },
    items: [
      { studentId: 1, studentName: 'Phêrô Trần Nguyễn Hoài An', nextProgramId: 37, amount: 10000 },
      { studentId: 2, studentName: 'Maria Nguyễn Ngọc Bảo Hân', nextProgramId: 37, amount: 10000 },
    ],
  }

  const { data: students = [], isFetching } = useGetMyStudentsQuery()
  const [createOrder, { isLoading: creating }] = useCreateOrderMutation()
  const { Loader } = useLoader()

  const [activeStep, setActiveStep] = useState(existingCode || previewMode ? 2 : 0)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [order, setOrder] = useState<OrderStatus | null>(null)
  const [error, setError] = useState('')

  const pollCode = previewMode ? '' : order?.paymentCode || existingCode || ''
  const { data: polledOrder } = useGetOrderStatusQuery(pollCode, {
    skip: !pollCode,
    pollingInterval: pollCode ? 3000 : 0,
  })

  const currentOrder = previewMode ? previewOrder : polledOrder || order

  const eligibleStudents = useMemo(
    () => students.filter((s) => !s.registered && !s.pendingPaymentCode && s.eligible),
    [students]
  )

  const selectedStudents = eligibleStudents.filter((s) => selectedIds.includes(s.studentId))
  const defaultFee = students[0]?.defaultFee || 200000
  const estimatedTotal = selectedStudents.reduce(
    (sum, s) => sum + (s.defaultFee || defaultFee),
    0
  )

  useEffect(() => {
    if (polledOrder?.status === 'PAID') {
      setActiveStep(2)
      dispatch(enrollmentApi.util.invalidateTags(['MyStudents']))
    }
  }, [polledOrder, dispatch])

  const toggleStudent = (student: EnrollStudent) => {
    setSelectedIds((prev) =>
      prev.includes(student.studentId)
        ? prev.filter((id) => id !== student.studentId)
        : [...prev, student.studentId]
    )
  }

  const handleCreateOrder = async () => {
    setError('')
    try {
      const result = await createOrder({ studentIds: selectedIds }).unwrap()
      setOrder(result)
      setActiveStep(2)
      navigate(`/ghi-danh?code=${result.paymentCode}`, { replace: true })
    } catch (e: any) {
      const raw = typeof e?.data === 'string' ? e.data : ''
      // Backend chặn kỳ ghi danh đã đóng trước khi xét điều kiện từng em, nên tới được
      // đây với một mã lý do nghĩa là ghi danh đang mở.
      setError(ineligibleMessage(raw, true) || raw || 'Không tạo được đơn thanh toán.')
    }
  }

  const qrRef = useRef<HTMLCanvasElement | null>(null)

  // Wrap a string to fit maxWidth, returning the individual lines.
  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
    const words = text.split(' ')
    const lines: string[] = []
    let current = ''
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word
      if (ctx.measureText(candidate).width > maxWidth && current) {
        lines.push(current)
        current = word
      } else {
        current = candidate
      }
    }
    if (current) {
      lines.push(current)
    }
    return lines
  }

  // Compose an information-rich PNG (QR + student names + amount + transfer memo +
  // bank account) so the saved image is self-explanatory in the phone gallery.
  const buildShareImage = async (): Promise<Blob | null> => {
    const qr = qrRef.current
    if (!qr || !currentOrder) {
      return null
    }

    const scale = 3
    const width = 380
    const pad = 24
    const contentWidth = width - pad * 2
    const qrSize = 300
    const qrBox = qrSize + 24 // white quiet zone around the QR for reliable scanning

    const names =
      currentOrder.items.map((i) => i.studentName).filter(Boolean).join(', ') || 'Đoàn sinh'
    const rows: { label: string; value: string; strong?: boolean }[] = [
      { label: 'Đoàn sinh', value: names },
      { label: 'Số tiền', value: formatVnd(currentOrder.expectedAmount), strong: true },
      { label: 'Nội dung CK', value: currentOrder.paymentCode, strong: true },
      { label: 'Số TK', value: currentOrder.qr.accountNo },
      { label: 'Chủ TK', value: currentOrder.qr.accountName },
    ]

    const measure = document.createElement('canvas').getContext('2d')
    if (!measure) {
      return null
    }
    const lineHeight = 24
    const rowGap = 8
    const rowFont = (strong?: boolean) =>
      `${strong ? '700' : '400'} ${strong ? 17 : 15}px Arial, sans-serif`

    let bodyHeight = 0
    const rowLines = rows.map((row) => {
      measure.font = rowFont(row.strong)
      const lines = wrapText(measure, `${row.label}: ${row.value}`, contentWidth)
      bodyHeight += lines.length * lineHeight + rowGap
      return lines
    })

    const titleHeight = 52
    const height = pad + titleHeight + qrBox + 16 + bodyHeight + pad

    const canvas = document.createElement('canvas')
    canvas.width = width * scale
    canvas.height = height * scale
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return null
    }
    ctx.scale(scale, scale)

    // Card background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)

    // Title
    ctx.fillStyle = '#1565c0'
    ctx.font = '700 20px Arial, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Mã QR chuyển khoản', width / 2, pad + 28)

    // QR on a white box (quiet zone), crisp (no smoothing) so modules stay sharp
    const qrX = (width - qrBox) / 2
    const qrY = pad + titleHeight
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(qrX, qrY, qrBox, qrBox)
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(qr, qrX + 12, qrY + 12, qrSize, qrSize)

    // Info rows
    ctx.textAlign = 'left'
    let y = qrY + qrBox + 16 + 18
    rows.forEach((row, idx) => {
      ctx.fillStyle = row.strong ? '#111111' : '#333333'
      ctx.font = rowFont(row.strong)
      rowLines[idx].forEach((line) => {
        ctx.fillText(line, pad, y)
        y += lineHeight
      })
      y += rowGap
    })

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png')
    })
  }

  const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigator as Navigator).maxTouchPoints > 1)

  const saveOrShareQr = async () => {
    const code = currentOrder?.paymentCode || 'thanh-toan'
    const blob = await buildShareImage()
    if (!blob) {
      return
    }
    const fileName = `ma-ck-${code}.png`
    const file = new File([blob], fileName, { type: 'image/png' })

    const nav = navigator as Navigator & {
      canShare?: (data?: ShareData) => boolean
    }

    // iOS Safari ignores the <a download> attribute, so the share sheet ("Lưu ảnh")
    // is the only reliable way to save to Photos there.
    if (isIOS() && nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: 'Mã QR chuyển khoản' })
        return
      } catch {
        // user huỷ hoặc share thất bại → rơi xuống tải về
      }
    }

    // Android + desktop: a programmatic anchor click downloads the file directly.
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Loader loading={isFetching || creating} />
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Ghi danh & thanh toán
          </Typography>
          <Button size="small" onClick={() => navigate('/home')}>
            Trang chủ
          </Button>
        </Stack>

        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error">{error}</Alert>}

        {activeStep === 0 && (
          <Stack spacing={2}>
            {eligibleStudents.length === 0 ? (
              <Alert severity="info">
                Không có đoàn sinh đủ điều kiện ghi danh. Chỉ những em có kết quả năm học trước mới ghi danh được — xem chi tiết ở trang chủ.
              </Alert>
            ) : (
              <>
                <Typography color="text.secondary">
                  Chọn (các) em cần đăng ký lớp cho năm học mới:
                </Typography>
                {eligibleStudents.map((student) => {
                  const checked = selectedIds.includes(student.studentId)
                  const fee = student.defaultFee || defaultFee
                  return (
                    <Box
                      key={student.studentId}
                      onClick={() => toggleStudent(student)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: '1px solid',
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: checked ? 'action.selected' : 'transparent',
                        borderRadius: 2,
                        p: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Checkbox checked={checked} sx={{ p: 0 }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>
                          {student.canonicalSaintName ? `${student.canonicalSaintName} ` : ''}
                          {student.fullName}
                        </Typography>
                        {student.nextProgramName && (
                          <Typography variant="body2" color="text.secondary">
                            {student.passedLastYear ? 'Đăng ký lớp' : 'Học lại'}:{' '}
                            {student.nextProgramName}
                          </Typography>
                        )}
                      </Box>
                      <Typography fontWeight={600} whiteSpace="nowrap">
                        {formatVnd(fee)}
                      </Typography>
                    </Box>
                  )
                })}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography color="text.secondary">
                    Đã chọn {selectedStudents.length} em
                  </Typography>
                  <Typography variant="h6">
                    Tổng cộng: {formatVnd(estimatedTotal)}
                  </Typography>
                </Box>
              </>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={selectedIds.length === 0}
              onClick={() => setActiveStep(1)}
            >
              Ghi danh
            </Button>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            {selectedStudents.map((s) => (
              <Typography key={s.studentId}>
                • {s.fullName}: {formatVnd(s.defaultFee || defaultFee)}
              </Typography>
            ))}
            <Typography variant="h6">Tổng: {formatVnd(estimatedTotal)}</Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Quay lại
              </Button>
              <Button variant="contained" onClick={handleCreateOrder}>
                Tạo mã QR thanh toán
              </Button>
            </Stack>
          </Stack>
        )}

        {activeStep === 2 && currentOrder && (
          <Stack spacing={2} alignItems="center">
            {currentOrder.status === 'PAID' ? (
              <Alert severity="success" sx={{ width: '100%' }}>
                Thanh toán thành công! Ghi danh đã hoàn tất.
              </Alert>
            ) : (
              <>
                <Typography textAlign="center">Quét mã QR để thanh toán.</Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: '100%' }}
                >
                  <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">Số TK: {currentOrder.qr.accountNo}</Typography>
                    <Typography variant="body2">Chủ TK: {currentOrder.qr.accountName}</Typography>
                    <Typography variant="body2">Nội dung: {currentOrder.paymentCode}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Số tiền cần chuyển
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        color="primary.main"
                        lineHeight={1.1}
                      >
                        {formatVnd(currentOrder.expectedAmount)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    <QRCodeCanvas
                      ref={qrRef}
                      value={currentOrder.qr.emvString}
                      size={360}
                      marginSize={4}
                      style={{ width: 150, height: 150 }}
                    />
                    <Button variant="contained" size="small" onClick={saveOrShareQr}>
                      Lưu mã để quét
                    </Button>
                  </Stack>
                </Stack>
                <Typography variant="body2" color="error" fontWeight={600} textAlign="center">
                  Vui lòng quét đúng mã QR này để thanh toán — hệ thống chỉ tự động ghi nhận khi thanh toán qua mã này.
                </Typography>
                <Alert severity="info" sx={{ width: '100%' }}>
                  <Typography variant="body2" fontWeight={600}>
                    Đang chờ thanh toán.
                  </Typography>
                  <Typography variant="body2">
                    Hệ thống tự xác nhận sau khi nhận được tiền.
                  </Typography>
                  <Typography variant="body2">
                    Quá vài phút chưa cập nhật, vui lòng liên hệ giáo xứ.
                  </Typography>
                </Alert>
              </>
            )}

            <Button variant="outlined" onClick={() => navigate('/home')}>
              Về trang chủ
            </Button>
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

export default EnrollmentWizard
